import Header from "../Common/Header";
import Sidebar from "../Common/Sidebar";
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import CommonValues from "../Common/utils";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import PerformanceRadar from "./PerformanceRadar";
import EvolutionFramework from "./EvolutionFramework";
import AssessmentOverview from "./AssessmentOverview";

interface Question {
  id: string;
  title: string;
  category: string;
  questionNumber: number;
}

interface Answer {
  score: number;
  notes?: string;
  question: Question;
}

interface ComponentScore {
  score: number;
  maxScore: number;
  percentage: number;
}
interface SummaryProps {
  initialAnswers?: Record<string, Answer>;
  transactionId?: string;
  suppressPost?: boolean; // don't call API when just viewing historical summary
  embedded?: boolean; // render without layout chrome (for dialog embedding)
  disableEmbeddedScroll?: boolean; // when true, do not constrain height when embedded
  agencyNameOverride?: string;
  agencyPhoneOverride?: string;
  agencyEmailOverride?: string;
}

export default function Summary({ initialAnswers, transactionId: propTransactionId, suppressPost, embedded, disableEmbeddedScroll, agencyNameOverride, agencyPhoneOverride, agencyEmailOverride }: SummaryProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [agencyName, setAgencyName] = useState("");
  const [agencyPhone, setAgencyPhone] = useState("");
  const [agencyEmail, setAgencyEmail] = useState("");
  const [transactionId, setTransactionId] = useState(propTransactionId || "");
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [totalScore, setTotalScore] = useState(0);
  const toast: any = useRef("");
  const navigate = useNavigate();

  useEffect(() => {
    // History dialog passes suppressPost + embedded; skip agency fetch and use overrides
    if (suppressPost) {
      if (agencyNameOverride) setAgencyName(agencyNameOverride);
      if (agencyPhoneOverride) setAgencyPhone(agencyPhoneOverride);
      if (agencyEmailOverride) setAgencyEmail(agencyEmailOverride);
      return;
    }
    getAMSAgencyDetails();
  }, [suppressPost, agencyNameOverride, agencyPhoneOverride, agencyEmailOverride]);
  // Initialize from props (embedded historical view) OR from navigation state (interactive flow)
  useEffect(() => {
    if (initialAnswers && Object.keys(initialAnswers).length > 0) {
      setAnswers(initialAnswers);
      if (propTransactionId) setTransactionId(propTransactionId);
    } else if (location.state) {
      setAnswers(location.state.answers || {});
      setTransactionId(location.state.transactionId || "");
    }
  }, [initialAnswers, propTransactionId, location.state]);

  useEffect(() => {
    const answerKeys = Object.keys(answers);
    if (answerKeys.length === 0) return;

    const totalScore = Object.values(answers)
      .filter((a) => a && typeof a.score === "number")
      .reduce((sum, a) => sum + a.score, 0);

    setTotalScore(totalScore);
    if (!suppressPost) {
      onProceedToQuestionClick(totalScore);
    }
  }, [answers]);

  const getAMSAgencyDetails = () => {
    setLoading(true);
    var token = localStorage.getItem("igtoken");
    var config = {
      method: "get",
      // url: `${process.env.REACT_APP_MAPSAPI}/api/nowcert/agencyinfo`,
      url: `${process.env.REACT_APP_MAPSAPI}/api/account/agencyinfo`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    axios(config)
      .then((response: any) => {
        // setAgencyInfo(response.data)
        setAgencyName(response.data.agencyName);
        setAgencyPhone(response.data.phone);
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
      });
  };
  const getScoreColor = (percentage: any) => {
    if (percentage >= 80) return "#22C55E";
    if (percentage >= 60) return "#84CC16";
    if (percentage >= 40) return "#FBBF24";
    return "#EF4444";
  };

  const getAreaEvaluation = (percentage: any) => {
    if (percentage >= 80) return "Strong Area";
    if (percentage >= 60) return "Solid Area";
    if (percentage >= 40) return "Development Area";
    return "Critical Focus Area";
  };
  const calculateComponentScores = (): Record<string, ComponentScore> => {
    const componentMapping: Record<string, string[]> = {
      Process: [
        "Sales Processes",
        "Servicing Processes",
        "Quote/Rate/Bind Processes",
        "Operations Processes",
        "Management/Reporting Processes",
        "Finance & Accounting Processes",
      ],
      "People/Workforce Management": ["People/Workforce Management"],
      "Enabling Technology": ["Enabling Technology"],
    };

    const scores: Record<string, ComponentScore> = {};

    Object.entries(componentMapping).forEach(([component, categories]) => {
      let total = 0;
      let count = 0;
      Object.values(answers).forEach((ans) => {
        if (ans && ans.question && categories.includes(ans.question.category)) {
          total += ans.score ?? 0;
          count++;
        }
      });

      const max = count * 5;
      scores[component] = {
        score: total,
        maxScore: max,
        percentage: max > 0 ? Math.round((total / max) * 100) : 0,
      };
    });

    return scores;
  };

  const getStage = (total: number) => {
    if (total >= 80) return { name: "Optimized Stage", description: "Exceptional operational capabilities" };
    if (total >= 60) return { name: "Systemized Stage", description: "Strong operational capabilities" };
    if (total >= 40) return { name: "Structured Stage", description: "Moderate operational capabilities" };
    return { name: "Emerging Stage", description: "Needs significant operational improvement" };
  };

  const showSummary = (isEmbedded: boolean) => {
    const stage = getStage(totalScore);
    const componentScores = calculateComponentScores();
    const grouped: Record<string, Answer[]> = {};
    Object.values(answers).forEach((ans) => {
      if (ans && ans.question && ans.question.category) {
        const cat = ans.question.category;
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(ans);
      }
    });
    const categoryMap: Record<string, string> = {
      "Sales Processes": "Sales",
      "Servicing Processes": "Servicing",
      "Quote/Rate/Bind Processes": "Quote/Rate/Bind",
      "Operations Processes": "Operations",
      "Management/Reporting Processes": "Management",
      "Finance & Accounting Processes": "Finance",
      "People/Workforce Management": "People",
      "Enabling Technology": "Technology",
    };

    const processGroups: Record<string, number> = {};

    Object.entries(grouped).forEach(([category, items]) => {
      const scores = items.map((item) => item.score || 0);
      const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;

      const shortLabel = categoryMap[category] || category;
      processGroups[shortLabel] = parseFloat(avgScore.toFixed(2));
    });

    const onSidebarClick = (val: any) => {
      setSidebarOpen(val);
    };
     const scheduleAppointment = () => {
      window.open(process.env.REACT_APP_Schedule_Appointment_Url, "_blank");
    };
    
    const summaryBody = (
      <div className={isEmbedded ? "" : "flex flex-col min-h-[calc(100vh-185px)] justify-center items-center"}>
        <div className={isEmbedded ? "p-4 bg-white rounded w-full" : "flex flex-col min-h-[calc(100vh-185px)] justify-center items-center"}>
          {/* Show Momentum Maps SVG logo only in embedded/dialog mode (inline for PDF export) */}
          {isEmbedded && (
            <div className="flex justify-center" aria-label="Momentum Maps Logo">
              <svg
                className="h-16 w-auto max-w-[220px]"
                viewBox="0 0 450 80"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
              >
                <title>Momentum Maps</title>
                <g>
                  <path d="M20 50 L40 40 L45 45 L45 70 L40 75 L20 65 Z" fill="#5BD584" />
                  <path d="M55 35 L75 25 L80 30 L80 70 L75 75 L55 65 Z" fill="#5BD584" />
                  <path d="M90 20 L110 10 L115 15 L115 70 L110 75 L90 65 Z" fill="#5BD584" />
                </g>
                <text x="135" y="45" fontFamily="Arial, sans-serif" fontSize="42" fontWeight="500" fill="#000000">
                  momentum
                </text>
                <text x="135" y="70" fontFamily="Arial, sans-serif" fontSize="32" fontWeight="400" fill="#5BD584">
                  maps
                </text>
              </svg>
            </div>
          )}
          <div className="p-6 md:p-10 bg-white rounded-xl shadow-md max-w-5xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-4">Diagnostic Summary</h2>

            <AssessmentOverview totalScore={totalScore} answers={answers} />
              <h2 className="text-3xl font-bold text-gray-800 text-center mb-4">Diagnostic Summary</h2>

              <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <h2 className="text-2xl font-bold text-gray-800">{agencyName || "Assessment Results"}</h2>
                <p className="text-base text-gray-500 mt-1">
                  {agencyPhone}
                  {agencyEmail && <span className="mx-2">•</span>}
                  {agencyEmail}
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mt-4">Overall Diagnostic Score</h3>
                <div className="mt-2 text-2xl font-bold text-green-600">{totalScore} / 100</div>
                <div className="mt-2 text-lg font-medium text-green-700">{stage.name}</div>
                <p className="text-base text-gray-600">{stage.description}</p>
              </div>
              {/* AssessmentOverview already rendered above (removed duplicate) */}
              <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <h3 className="text-2xl font-bold mb-2">Transform Your Diagnostic Results Into Action</h3>
                <p className="text-base">
                  <strong>Congratulations on completing your Momentum Diagnostic!</strong>
                  <br /> You've taken the crucial first step in understanding your agency's performance.
                </p>
              </div>

              <div className="mb-8 text-center">
                <h3 className="text-2xl font-bold text-gray-800 mb-1">Your Performance Score Card</h3>
                <p className="text-base mb-4">Click the MAPS Senior Advisor tile below to unlock your agency's potential</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(componentScores).map(([comp, data]) => (
                    <div key={comp} className="bg-gray-50 border border-gray-200 p-5 rounded-lg shadow-sm">
                      <h4 className="text-lg font-bold text-gray-700 mb-1">{comp}</h4>
                      <div className="text-2xl font-semibold" style={{ color: getScoreColor(data.percentage) }}>
                        {data.percentage}%
                      </div>
                      <p className="text-sm text-gray-500">
                        {data.score} / {data.maxScore}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">{getAreaEvaluation(data.percentage)}</p>
                    </div>
                  ))}

                  <div
                    onClick={scheduleAppointment}
                    className="maps-advisor-tile group bg-gray-50 border border-gray-200 p-5 rounded-lg shadow-sm cursor-pointer transition-all duration-300 ease-in-out"
                  >
                    <h4 className="text-lg font-semibold group-hover:text-white">MAPS Senior Advisor</h4>
                    <p className="font-bold italic text-xl mb-4 leading-snug group-hover:text-white">
                      No Cost <br /> or Obligation
                    </p>
                    <p className="font-semibold text-lg group-hover:text-white">Strategic Consultation</p>
                    <p className="mt-2 text-sm leading-snug group-hover:text-white">
                      Get Your Personalized <br /> Action Plan Today
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-base">
                <p>
                  Your results reveal specific opportunities to enhance efficiency, boost productivity, and accelerate growth. But insights without
                  action won't drive change.
                </p>
              </div>

              <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="space-y-4 text-center">
                  <h3 className="text-3xl font-semibold leading-snug">
                    Speak with a MAPS
                    <br />
                    Senior Advisor
                  </h3>
                  <p className="text-base leading-relaxed">
                    Let our Managed & Professional Services (MAPS) Senior Advisors turn these insights into your competitive advantage.
                    <br /> Schedule a{" "}
                    <span className="text-[#5BD584] font-bold italic underline text-lg cursor-pointer" onClick={scheduleAppointment}>
                      No Cost or Obligation Consultation
                    </span>{" "}
                    and receive:
                  </p>
                  <p className="text-[#5BD584] text-xl font-semibold my-5 leading-relaxed">
                    ✓ Strategic Recommendations <br /> ✓ Expert Guidance <br /> ✓ Personalized Action Plan
                  </p>
                  <div className="text-center">
                    <span
                      className="text-sm font-medium text-black bg-[#5BD584] py-2 px-3 rounded cursor-pointer transition"
                      onClick={scheduleAppointment}
                    >
                      No Cost • No Obligation • Schedule Now
                    </span>
                  </div>
                </div>
              </div>
              <h2 className="flex items-center gap-3 text-3xl font-bold text-momentum-dark mb-8">
                <div className="w-1 h-6 bg-momentum-accent rounded-sm"></div>
                Performance Radar
              </h2>
              <PerformanceRadar processGroups={processGroups} />
              <div>
                <h2 className="flex items-center gap-3 text-3xl font-bold text-momentum-dark mb-8">
                  <div className="w-1 h-6 bg-momentum-accent rounded-sm"></div>
                  Detailed Assessment by Operational Area
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
                  {Object.entries(grouped).map(([cat, items], index) => {
                    let shortLabel = cat === "Sales Processes" ? "S" : cat === "Servicing Processes" ? "SV" : cat.charAt(0);
                    return (
                      <div key={cat} className="bg-card p-6 rounded-xl hover-lift border-2 border-momentum-accent/40">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-momentum-dark">
                            {index + 1}. {cat}
                          </h3>
                          <div className="w-10 h-10 bg-momentum-gradient rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {shortLabel}
                          </div>
                        </div>
                        <div className="space-y-3">
                          {items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between py-3 border-b border-border last:border-b-0">
                              <span className="text-foreground flex-1 pr-4">{item.question.title}</span>
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-semibold min-w-[45px] text-center text-white ${
                                  item.score === 1
                                    ? "score-1"
                                    : item.score === 2
                                    ? "score-2"
                                    : item.score === 3
                                    ? "score-3"
                                    : item.score === 4
                                    ? "score-4"
                                    : item.score === 5
                                    ? "score-5"
                                    : "bg-gray-300"
                                }`}
                              >
                                {item.score}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <EvolutionFramework total={totalScore} />
              </div>

              <div className="text-center schedule-btn">
                <button
                  onClick={scheduleAppointment}
                  className="mt-6 w-auto bg-green-500 hover:bg-green-600 text-white text-base font-semibold py-2 px-4 rounded transition"
                >
                  Schedule Appointment
                </button>
              </div>
          </div>
        </div>
      </div>
    );

    if (isEmbedded) {
      const embeddedClass = disableEmbeddedScroll
        ? "summary-embedded-content"
        : "summary-embedded-content overflow-y-auto max-h-[75vh]";
      return <div className={embeddedClass}>{summaryBody}</div>;
    }
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Toast ref={toast} />
        <Sidebar pagename="summary" onSidebarClick={() => onSidebarClick(false)} sidebarOpen={sidebarOpen} />
        <main className="flex-1 h-screen overflow-y-auto p-4 md:p-8">
          <Header onSidebarClick={() => onSidebarClick(true)} />
          {summaryBody}
        </main>
      </div>
    );
  };
  const onProceedToQuestionClick = (score: any) => {
    // setSaveLoading(true);
    var data = {
      transactionId: transactionId,
      questionnaire: JSON.stringify(answers),
      diagnosticScore: score.toString(),
      completedDate: null,
    };
    var config = {
      method: "post",
      url: `${process.env.REACT_APP_MAPSAPI}/api/account/questionnaire`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {})
      .catch((error: any) => {
        // setSaveLoading(false);
        if (error.response != null) {
          if (error.response.status === 401) {
            CommonValues.Logout(navigate);
          }
          // else if (error.response.status === 404) {
          //   toast.current.show({ severity: "error", summary: "Error", detail: "Unknown error while proceeding to question", life: 3000 });
          // } else {
          //   toast.current.show({ severity: "error", summary: "Error", detail: "Unknown error while proceeding to question", life: 3000 });
          // }
        }
      });
  };
  return showSummary(!!embedded);
}
